// Vendored from the JETCAM DXF Viewer (src/geometry/measure.ts), 2026-09-02.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Local edits are marked "dxfsplitter:". Do not reformat; keep diffable against origin.

import { type ArcPrimitive, type PolylinePrimitive, type Primitive } from "./types.ts";
import { sweepOf } from "./extents.ts";

/**
 * Lengths of primitives, in drawing units.
 *
 * Lives in `geometry/` rather than in the renderer because it is pure geometry
 * and because Phase 6's cut-length statistics need exactly the same numbers.
 * Two implementations of "how long is this arc" would eventually disagree, and
 * the one the user sees in a tooltip should be the one the report totals.
 */

export function arcLength(p: ArcPrimitive): number {
  return p.radius * sweepOf(p);
}

export function polylineLength(p: PolylinePrimitive): number {
  const pts = p.points;
  let total = 0;
  for (let i = 0; i + 1 < pts.length; i += 1) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    total += Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
  }
  if (p.closed && pts.length > 2) {
    const a = pts[pts.length - 1]!;
    const b = pts[0]!;
    total += Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
  }
  return total;
}

/** Length of a primitive, or 0 for those that have none (points, text). */
export function primitiveLength(p: Primitive): number {
  switch (p.kind) {
    case "line":
      return Math.hypot(p.b.x - p.a.x, p.b.y - p.a.y, p.b.z - p.a.z);
    case "polyline":
      return polylineLength(p);
    case "arc":
      return arcLength(p);
    case "point":
    case "text":
      return 0;
  }
}
